import { useState } from 'react';
import { Button, CheckboxGroup, Fieldset, HStack, Input, Stack, Text } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import { Radio, RadioGroup } from '@/components/ui/radio';
import { toaster } from '@/components/ui/toaster';
import useApiKeysStore from '@/utils/use-api-keys';
import { DailogProps } from '@/lib/types';
import { SCOPE_CATALOG, SCOPE_READ_ALL } from '@/lib/api-key-scopes';
import ApiKeySecretReveal from './api-key-secret-reveal';

const schema = z.object({
  name: z
    .string()
    .min(1, 'Required')
    .max(100, 'Max 100 characters')
    .refine((v) => v.trim().length > 0, 'Required'),
});

type FormValues = z.infer<typeof schema>;

type ExpiryPreset = 'never' | '30' | '90' | 'custom';

function expiryFromPreset(preset: ExpiryPreset, customDate: string): string | null {
  if (preset === 'never') return null;
  if (preset === 'custom') {
    if (!customDate) return null;
    const d = new Date(customDate);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }
  const days = preset === '30' ? 30 : 90;
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export default function CreateApiKeyDialog({ isOpen, onClose }: DailogProps) {
  const { workspaceId } = useParams();
  const createApiKey = useApiKeysStore((s) => s.createApiKey);
  const [secret, setSecret] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [scopes, setScopes] = useState<string[]>([SCOPE_READ_ALL]);
  const [expiryPreset, setExpiryPreset] = useState<ExpiryPreset>('never');
  const [customDate, setCustomDate] = useState('');
  const [scopeError, setScopeError] = useState<string | null>(null);
  const hasReadAll = scopes.includes(SCOPE_READ_ALL);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { name: '' } });

  function handleClose() {
    reset();
    setSecret(null);
    setSubmitting(false);
    setScopes([SCOPE_READ_ALL]);
    setExpiryPreset('never');
    setCustomDate('');
    setScopeError(null);
    onClose();
  }

  async function onSubmit(values: FormValues) {
    if (!workspaceId) return;
    if (scopes.length === 0) {
      setScopeError('Select at least one scope');
      return;
    }
    const expiresAt = expiryFromPreset(expiryPreset, customDate);
    if (expiryPreset === 'custom' && expiresAt === null) {
      toaster.create({
        type: 'error',
        title: 'Invalid expiration date',
        description: 'Pick a valid date in the future.',
      });
      return;
    }
    if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
      toaster.create({
        type: 'error',
        title: 'Invalid expiration date',
        description: 'Expiration must be in the future.',
      });
      return;
    }
    setSubmitting(true);
    try {
      const created = await createApiKey(workspaceId, {
        name: values.name.trim(),
        scopes,
        expires_at: expiresAt,
      });
      setSecret(created.key);
      toaster.create({ type: 'success', title: 'API key created' });
    } catch (e: any) {
      toaster.create({
        type: 'error',
        title: 'Could not create API key',
        description: e?.response?.data?.error,
      });
      setSubmitting(false);
    }
  }

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(d) => (!d.open ? handleClose() : null)}>
      <DialogBackdrop />
      <DialogContent>
        <DialogCloseTrigger />
        {secret === null ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>Create API key</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Stack gap={5}>
                <Field
                  label='Name'
                  required
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                  helperText='Give the key a recognizable name so you can identify it later.'
                >
                  <Input
                    placeholder='e.g. Production sync'
                    autoFocus
                    {...register('name')}
                  />
                </Field>

                <Fieldset.Root invalid={!!scopeError}>
                  <Fieldset.Legend>
                    Scopes <Text as='span' color='danger'>*</Text>
                  </Fieldset.Legend>
                  <CheckboxGroup
                    value={hasReadAll ? SCOPE_CATALOG.map((s) => s.value) : scopes}
                    onValueChange={(value) => {
                      setScopeError(null);
                      const next = value as string[];
                      const nextHasReadAll = next.includes(SCOPE_READ_ALL);
                      if (nextHasReadAll && !hasReadAll) {
                        // Turning on the wildcard collapses to just [read:all]
                        // — the backend stores it that way, and any new scopes
                        // added later are auto-granted.
                        setScopes([SCOPE_READ_ALL]);
                      } else if (!nextHasReadAll && hasReadAll) {
                        // Turning off the wildcard clears selection so the
                        // admin explicitly picks specific scopes.
                        setScopes([]);
                      } else {
                        setScopes(next);
                      }
                    }}
                  >
                    <Fieldset.Content>
                      {SCOPE_CATALOG.map((option) => {
                        const isReadAll = option.value === SCOPE_READ_ALL;
                        return (
                          <Checkbox
                            key={option.value}
                            value={option.value}
                            disabled={hasReadAll && !isReadAll}
                          >
                            <Stack gap={0}>
                              <Text fontSize='sm' fontWeight='medium'>
                                {option.label}
                              </Text>
                              <Text fontSize='xs' color='fg.muted'>
                                {option.description}
                              </Text>
                            </Stack>
                          </Checkbox>
                        );
                      })}
                    </Fieldset.Content>
                  </CheckboxGroup>
                  {scopeError && (
                    <Fieldset.ErrorText>{scopeError}</Fieldset.ErrorText>
                  )}
                </Fieldset.Root>

                <Field label='Expiration'>
                  <RadioGroup
                    value={expiryPreset}
                    onValueChange={(d) => setExpiryPreset(d.value as ExpiryPreset)}
                  >
                    <Stack gap={2}>
                      <Radio value='never'>Never</Radio>
                      <Radio value='30'>In 30 days</Radio>
                      <Radio value='90'>In 90 days</Radio>
                      <Radio value='custom'>Custom date</Radio>
                    </Stack>
                  </RadioGroup>
                  {expiryPreset === 'custom' && (
                    <Input
                      type='date'
                      mt={2}
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                    />
                  )}
                </Field>
              </Stack>
            </DialogBody>
            <DialogFooter>
              <HStack>
                <Button variant='ghost' onClick={handleClose} type='button'>
                  Cancel
                </Button>
                <Button colorPalette='brand' type='submit' loading={submitting}>
                  Create key
                </Button>
              </HStack>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Copy your API key</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <ApiKeySecretReveal secret={secret} />
            </DialogBody>
            <DialogFooter>
              <Button colorPalette='brand' onClick={handleClose}>
                I&apos;ve saved it
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </DialogRoot>
  );
}
